import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import pageActions from 'core/page/actions';
import dataActions from 'core/data/actions';
import { withStyles } from 'theme/withStyles';
import BottomContent from 'common/BottomContent';
import hasError from 'utils/hasError';
import ErrorMsg from 'common/ErrorMsg';
import RawHtml from 'common/RawHtml';
import ListGroup from 'containers/ListGroup';
import ButtonRowGroup from './common/ButtonRowGroup';

import commonStyles from './styles';

const styles = (theme) => ({
  ...commonStyles(theme),
  addUser: {
    display: 'block',
    margin: '50px auto 0',
  },
});

const BeneficiariesBare = ({
  bottomContent,
  classes,
  disableButton,
  hasFields,
  isCurrentlyEditing,
  label,
  labelMobile,
  listGroup,
  listLength,
  name,
  onAdd,
  onEdit,
  onRemove,
  onSave,
  subText,
}) => (
  <>
    <legend
      className={classnames('grp-label', classes.label)}
      role={!hasFields ? 'presentation' : undefined}
    >
      <RawHtml type="span" className={classnames('desktop', classes.desktopLabel)}>
        {label}
      </RawHtml>
      <RawHtml type="span" className={classnames('mobile', classes.mobileLabel)}>
        {labelMobile}
      </RawHtml>
    </legend>
    <div className="beneficiaries-group-view">
      <RawHtml className={classnames('content', classes.content)}>{subText}</RawHtml>

      <ErrorMsg name={name} type="group" />

      {listLength > 0 && (
        <ListGroup
          groupName={name}
          onRemove={onRemove}
          onEdit={onEdit}
          onDone={onSave}
          disableButton={disableButton}
        />
      )}

      {!isCurrentlyEditing && (
        <RawHtml
          className={classnames('btn btn-mobile btn-tertiary btn-add-beneficiary', classes.addUser)}
          data-name="btn-add-beneficiary"
          type="button"
          onClick={onAdd}
          variant="secondary"
          disabled={disableButton}
        >
          {listGroup.addButtonLabel}
        </RawHtml>
      )}

      <BottomContent className="bottomContent">{bottomContent}</BottomContent>

      <ButtonRowGroup groupName={name} />
    </div>
  </>
);

BeneficiariesBare.propTypes = {
  bottomContent: PropTypes.string,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  disableButton: PropTypes.bool,
  hasFields: PropTypes.bool,
  isCurrentlyEditing: PropTypes.bool,
  label: PropTypes.string,
  labelMobile: PropTypes.string,
  listGroup: PropTypes.shape({
    addButtonLabel: PropTypes.string,
  }),
  listLength: PropTypes.number,
  name: PropTypes.string.isRequired,
  onAdd: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  subText: PropTypes.string,
};

BeneficiariesBare.defaultProps = {
  bottomContent: '',
  disableButton: false,
  hasFields: false,
  isCurrentlyEditing: false,
  label: '',
  labelMobile: '',
  listGroup: {},
  listLength: 0,
  subText: '',
};

const BeneficiariesStyled = withStyles(styles)(BeneficiariesBare);

const hasIncompleteRequiredFields = (data, fields, groupFields, groupName) => {
  if (!groupFields || groupFields.length === 0) return false;

  const listData = data[`${groupName}___list`];
  if (listData && listData.length > 0) {
    const editingItem = listData.find((item) => item.isEditing || item.isNew);
    if (editingItem) {
      return groupFields.some((fieldName) => {
        const field = fields[`${fieldName}:0`] || fields[fieldName];
        if (!field || field.hidden || !field.required) return false;

        const fieldValue = editingItem[fieldName];
        const isEmpty =
          fieldValue === '' ||
          fieldValue === null ||
          fieldValue === undefined ||
          (field.fieldType === 'BOOLEAN' && fieldValue === false);

        return isEmpty;
      });
    }
  }

  return groupFields.some((fieldName) => {
    const field = fields[fieldName];
    if (!field || field.hidden || !field.required) return false;

    const fieldValue = data[fieldName];
    const isEmpty =
      fieldValue === '' ||
      fieldValue === null ||
      fieldValue === undefined ||
      (field.fieldType === 'BOOLEAN' && fieldValue === false);

    return isEmpty;
  });
};

const hasUserStartedEditing = (data, fields, groupFields, groupName) => {
  if (!groupFields || groupFields.length === 0) return false;

  const listData = data[`${groupName}___list`];
  if (listData && listData.length > 0) {
    const hasEditingItem = listData.some((item) => item.isEditing || item.isNew);
    if (hasEditingItem) {
      const editingItem = listData.find((item) => item.isEditing || item.isNew);
      return groupFields.some((fieldName) => {
        const field = fields[`${fieldName}:0`] || fields[fieldName];
        if (!field || field.hidden) return false;

        const fieldValue = editingItem[fieldName];
        return fieldValue !== '' && fieldValue !== null && fieldValue !== undefined;
      });
    }
  }

  return groupFields.some((fieldName) => {
    const field = fields[fieldName];
    if (!field || field.hidden) return false;

    const fieldValue = data[fieldName];
    return fieldValue !== '' && fieldValue !== null && fieldValue !== undefined;
  });
};

const mapStateToProps = ({ data, fields, groups }, { name }) => {
  const group = groups[name];
  const hasFieldError = hasError(fields);
  const listLength = Object.values(data[`${name}___list`])?.length || 0;
  const { maxItems } = group.listGroup;
  const isMaxReached = listLength >= maxItems;

  const hasActiveFields = group.fields?.some(
    (fieldName) => fields[fieldName] !== undefined || fields[`${fieldName}:0`] !== undefined,
  );

  const userStartedEditing =
    hasActiveFields && hasUserStartedEditing(data, fields, group.fields, name);

  const hasIncompleteFields = hasActiveFields
    ? hasIncompleteRequiredFields(data, fields, group.fields, name)
    : false;

  const listData = data[`${name}___list`];
  const isCurrentlyEditing =
    listData && listData.length > 0 ? listData.some((item) => item.isEditing || item.isNew) : false;

  const disableButton =
    (isCurrentlyEditing && (!userStartedEditing || hasIncompleteFields)) ||
    hasFieldError ||
    isMaxReached;

  return {
    bottomContent: group.bottomContent,
    disableButton,
    hasFields: group?.fields?.length > 0 ?? false,
    isCurrentlyEditing,
    label: group.label,
    labelMobile: group.labelMobile,
    listGroup: group.listGroup,
    listLength,
    subText: group.subText,
  };
};

const mapDispatchToProps = (dispatch) => ({
  onAdd: () => dispatch(pageActions.addBeneficiary()),
  onEdit: (...args) => dispatch(dataActions.changeListGroupData(...args)),
  onRemove: (index) => dispatch(pageActions.removeBeneficiary(index)),
  onSave: (groupName, index) => dispatch(pageActions.saveBeneficiary(index)),
});

export default connect(mapStateToProps, mapDispatchToProps)(BeneficiariesStyled);
